/**
 * @file RowActionsMenu.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-06-12
 * @modified 2026-10-21
 * @description Single-button row actions menu (macOS-context-menu style). A
 *              compact trigger button reveals an animated dropdown that opens
 *              downward from the button and grows right-to-left (its right edge
 *              pinned under the button) so it can be as wide as the widest
 *              action label needs. Rendered through a portal to document.body
 *              so the table's overflow:auto can't clip it. Disabled actions are
 *              shown but visually muted and non-interactive.
 *
 *              actions: Array<{
 *                key?: string, icon: string, label: string,
 *                onClick?: (e) => void, disabled?: boolean,
 *                title?: string, className?: string
 *              }>
 */

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './RowActionsMenu.css';
import useReadOnly from '../hooks/useReadOnly';
import { applyReadOnly } from '../utils/readOnlyActions';
import { placeVertically } from '../utils/menuPlacement';

function RowActionsMenu({
  actions: rawActions = [],
  triggerIcon = 'more_vert',
  triggerTitle = 'Actions',
  buttonClassName = 'btn-icon'
}) {
  // Default-deny for view-only accounts: every action is disabled unless it
  // marked itself readSafe. See utils/readOnlyActions.js for why the default
  // runs this way round.
  const readOnly = useReadOnly();
  const actions = applyReadOnly(rawActions, readOnly);

  const [open, setOpen] = useState(false);
  const [deployed, setDeployed] = useState(false);
  const [coords, setCoords] = useState({ top: 0, right: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const closeTimer = useRef(null);

  const close = useCallback(() => {
    setDeployed(false);
    clearTimeout(closeTimer.current);
    // Let the fade/scale-out transition finish before unmounting the portal.
    closeTimer.current = setTimeout(() => setOpen(false), 160);
  }, []);

  const anchorRef = useRef(null);
  const reposition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    anchorRef.current = { top: r.top, bottom: r.bottom };
    // Pin the menu's right edge just under the trigger so it grows leftward;
    // flipped above it once measured if it would run off the panel's foot.
    setCoords({ top: r.bottom + 4, right: Math.max(4, window.innerWidth - r.right) });
  }, []);

  // Once the menu is on the page and its height is known: below the trigger
  // when it fits above the status bar, else above it (utils/menuPlacement).
  useLayoutEffect(() => {
    if (!open || !menuRef.current || !anchorRef.current) return;
    const h = menuRef.current.offsetHeight;
    const top = placeVertically({ anchorTop: anchorRef.current.top, anchorBottom: anchorRef.current.bottom, height: h, gap: 4 });
    setCoords(c => (c.top === top ? c : { ...c, top }));
  }, [open]);

  const toggle = useCallback((e) => {
    e.stopPropagation();
    if (open) { close(); return; }
    clearTimeout(closeTimer.current);
    reposition();
    setOpen(true);
    requestAnimationFrame(() => setDeployed(true));
  }, [open, close, reposition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocPointer = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      close();
    };
    const onScroll = () => close();
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onResize = () => close();
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('scroll', onScroll, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const handleItem = (e, action) => {
    e.stopPropagation();
    if (action.disabled) return;
    close();
    if (action.onClick) action.onClick(e);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`${buttonClassName} row-actions-trigger${open ? ' open' : ''}`}
        title={triggerTitle}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        <span className="material-symbols-rounded">{triggerIcon}</span>
      </button>

      {open && ReactDOM.createPortal(
        <div
          ref={menuRef}
          className={`row-actions-menu${deployed ? ' deployed' : ''}`}
          style={{ top: coords.top, right: coords.right }}
          role="menu"
          onClick={(e) => e.stopPropagation()}
        >
          {actions.map((a, i) => (
            <button
              key={a.key || i}
              type="button"
              role="menuitem"
              className={`row-actions-item${a.disabled ? ' disabled' : ''}${a.className ? ` ${a.className}` : ''}`}
              disabled={a.disabled}
              title={a.title || a.label}
              onClick={(e) => handleItem(e, a)}
            >
              <span className="material-symbols-rounded">{a.icon}</span>
              <span className="row-actions-label">{a.label}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

export default RowActionsMenu;
